/**
 * Modelling Costs — the accounts-anchored two-table cost model (Chris, 2026-09-01,
 * "fully rebuild the costs engine"). The PRIMARY cost mechanism now:
 *
 *   Table 1 — Modelling source : the real basis-season Management P&L, month by
 *             month, as expense lines + departmental labour + an Overrides &
 *             Additions section. Each line has an include tick and a Fixed/Variable
 *             behaviour; variable lines carry a driver.
 *   Table 2 — After the build   : the same lines re-priced by the designed
 *             timetable's activity, with the per-line differential. FIXED lines
 *             carry through 100% but each has a FLEX SLIDER capped at the season
 *             turns-reduction, so a chosen share of that proportional saving can be
 *             taken off a line.
 *
 * The legacy rate-card tables live in an accordion beneath these (rendered by the
 * parent). Data: GET /api/research/studio-costs. Config persists in the shared
 * Studio defaults (app.StudioDefaults defaults.costConfig).
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Btn, Callout, PillGroup } from '../ui'
import {
  buildSim, simulate, pnlReconciliation, DRIVER_LABEL,
  type Activity, type CostBehaviour, type CostConfig, type CostDriver, type CustomFixedLine,
  type CostLine, type CostLineOverride, type LineResult, type RollingStockInput, type StudioCostsResponse,
} from '../costLines'
import { LOCO_CATEGORY, ROLLING_STOCK_CATEGORY, RS_SPREAD_ID, type EngineActivity } from '../fleetRegister'

// The grid shows plain numbers — every figure is £ and the heading says so
// (Chris, 2026-10-28: the £ signs cost horizontal space).
const gbp0 = (n: number) => (n < 0 ? '-' : '') + Math.round(Math.abs(n)).toLocaleString('en-GB')
/** The Rate column shows just the number; the full wording is the hover. */
const rateNum = (label: string) => label.match(/[\d][\d,.]*/)?.[0] ?? label
const DRIVERS: CostDriver[] = ['runningDays', 'workings', 'trainMiles', 'passengers']

export interface CostActivity {
  basis: Activity[]
  model: Activity[]
  /** Month-sides whose snapshot predates per-segment mileage — their train-miles
   *  fell back to the legacy 24 mi/turn proxy. Named on screen (law 8). */
  proxyRows?: number
  /** Each register engine's basis-season timetabled miles and worked days by
   *  month, from today's timetable — what its named hire lines are priced on. */
  basisEngines?: EngineActivity
}

export interface ModellingCostsProps {
  /** The basis-season P&L, fetched by the orchestrator (one source for the tables
   *  AND the exec PDF). */
  data: StudioCostsResponse | null
  loading: boolean
  err: string | null
  basis: string
  basisOptions: { value: string; label: string }[]
  onBasisChange: (b: string) => void
  /** Designed-vs-current activity by operating month (0 = March). */
  activity: CostActivity
  /** Persisted line config (overrides + additions). */
  config: CostConfig
  onConfigChange: (c: CostConfig) => void
  onPersist: () => void
  defaultsBusy: boolean
  /** Legacy rate-card category £/month, seeded into Overrides & Additions
   *  (kept & editable, unchecked by default so they never double-count the P&L). */
  legacySeed: CostLine[]
  /** The rolling-stock register (Chris, 2026-09-11): the engines ticked "Use in
   *  simulation", the months' running days and the basis engine activity. Their
   *  named lines and the "Fixed Rolling Stock" spread come off Hire of Rolling
   *  Stock; what remains is "Variable after Fixed". */
  rolling?: RollingStockInput
  /** Which table (Chris, 2026-10-28: "one gigantic page"): 'source' = Costs
   *  (Pre-Modelling), the basis P&L; 'modelled' = Costs (Results), after the
   *  build. Unset = both, stacked. */
  view?: 'source' | 'modelled'
}

const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']

export function ModellingCosts(props: ModellingCostsProps) {
  const { data, loading, err, basis, basisOptions, onBasisChange, activity, config, onConfigChange, onPersist, defaultsBusy, legacySeed, rolling, view } = props
  const [editing, setEditing] = useState(false)
  /** Enable Flex (Chris, 2026-10-28): the Flex sliders sat in the last column,
   *  which moved as the figures changed width — "the flex slider is jumping
   *  from right to left". Toggled on, a fixed-width Flex column is frozen
   *  straight after Line on the Results table; off, there is no Flex column. */
  const [flexOn, setFlexOn] = useState(false)

  // One assembly for the tables AND the exec PDF (buildSim). Until a season is
  // built there is no designed activity, so Table 2 mirrors Table 1 (no spurious
  // £0 variable lines, no phantom 100% flex cap).
  const { sim, hasModel, warnings } = useMemo(
    () => (data
      ? buildSim(data, config, legacySeed, activity.basis, activity.model, rolling)
      : { sim: simulate([], activity.basis, activity.basis), lines: [] as CostLine[], hasModel: false, warnings: [] as string[] }),
    [data, config, legacySeed, activity, rolling]
  )
  const flexPct = Math.round(sim.flexCap * 100)

  // --- config mutation -------------------------------------------------------
  const patch = (id: string, ov: Partial<CostLineOverride>) => {
    const overrides = { ...(config.overrides ?? {}) }
    overrides[id] = { ...(overrides[id] ?? {}), ...ov }
    onConfigChange({ ...config, overrides })
  }
  const toggleInclude = (l: CostLine) => patch(l.id, { included: !l.included })
  const setBehaviour = (l: CostLine, b: CostBehaviour) => patch(l.id, { behaviour: b })
  const setDriver = (l: CostLine, d: CostDriver) => patch(l.id, { driver: d })
  const setFlex = (l: CostLine, f: number) => patch(l.id, { flex: f })
  // "Fixed Rolling Stock" (Chris, 2026-09-11): the Spread cell is a season £ of
  // hire that is really fixed, shared equally over the running months; a figure
  // typed into a month cell stands for that month instead.
  const rs = config.rollingStock ?? {}
  const setSpread = (v: number) => onConfigChange({ ...config, rollingStock: { ...rs, spread: Number.isFinite(v) ? Math.max(0, v) : 0 } })
  const setSpreadCell = (i: number, v: number | null) => {
    const spreadMonths = { ...(rs.spreadMonths ?? {}) }
    if (v == null || !Number.isFinite(v)) delete spreadMonths[i]; else spreadMonths[i] = Math.max(0, v)
    onConfigChange({ ...config, rollingStock: { ...rs, spreadMonths } })
  }
  const isRolling = (l: CostLine) => l.category === ROLLING_STOCK_CATEGORY || l.category === LOCO_CATEGORY
  // The planner's CUSTOM FIXED LINES (Chris, 2026-09-11): "+ fixed line" under
  // any P&L line while editing; named, Flat or Cost-curve, a Starter £; each
  // comes off the line it sits under.
  const fixedLines = config.fixedLines ?? []
  const fixedById = new Map(fixedLines.map((f) => [f.id, f]))
  const addFixedLine = (parentId: string) => {
    const id = `fx:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
    onConfigChange({ ...config, fixedLines: [...fixedLines, { id, parentId, label: 'New fixed line', mode: 'flat', starter: 0 }] })
  }
  const patchFixedLine = (id: string, p: Partial<CustomFixedLine>) => onConfigChange({ ...config, fixedLines: fixedLines.map((f) => (f.id === id ? { ...f, ...p } : f)) })
  const removeFixedLine = (id: string) => onConfigChange({ ...config, fixedLines: fixedLines.filter((f) => f.id !== id) })
  /** A P&L line a custom fixed line may be added under: an accounts line (or what
   *  remains of one), never a memo, a deducted line or the register's lines. */
  const canHost = (l: CostLine) => (l.kind === 'expense' || l.kind === 'labour') && !l.memoOf && !l.customFixed && !l.engineId && l.id !== RS_SPREAD_ID

  // --- grouped rows ----------------------------------------------------------
  const groups: { title: string; kind: 'expense' | 'labour' | 'override'; rows: LineResult[] }[] = useMemo(() => {
    // Each engine's hire, fixed fee and own fuel sit in their own group
    // (Chris, 2026-10-28: "separating the locos into their own grouping").
    const loco = sim.lines.filter((r) => r.line.engineId)
    const exp = sim.lines.filter((r) => r.line.kind === 'expense' && !r.line.engineId)
    const lab = sim.lines.filter((r) => r.line.kind === 'labour')
    const ovr = sim.lines.filter((r) => r.line.kind === 'override')
    return [
      { title: 'Direct & indirect expenses (Management P&L)', kind: 'expense', rows: exp },
      ...(loco.length > 0 ? [{ title: 'Locomotives — hire, fixed fees and own fuel (Rolling stock in use)', kind: 'expense' as const, rows: loco }] : []),
      { title: 'Departmental labour', kind: 'labour', rows: lab },
      { title: 'Overrides & Additions', kind: 'override', rows: ovr },
    ]
  }, [sim])

  const inclTotalBasis = sim.basisTotal
  const inclTotalModel = sim.modelTotal

  if (err) {
    return (
      <section className="sh-card sh-card--pad sh-mt">
        <div className="sh-section-label">Modelling costs</div>
        <div className="stu-note" style={{ color: '#b03535' }}>Could not load the Management P&amp;L costs: {err}</div>
      </section>
    )
  }

  return (
    <section className="sh-card sh-card--pad sh-mt">
      {/* No title bar — the tab names the table (Chris, 2026-10-28: more grid on screen). */}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '6px 12px' }}>
        <label className="sh-mono" style={{ fontSize: 12 }}>
          Cost basis&nbsp;
          <select value={basis} onChange={(e) => onBasisChange(e.target.value)}>
            {basisOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {data?.last_refresh && <span className="sh-dim" style={{ fontSize: 11 }}>P&amp;L pulled {new Date(data.last_refresh).toLocaleDateString('en-GB')}</span>}
        {loading && <span className="sh-dim">loading…</span>}
        <Btn kind={editing ? 'gold' : 'ghost'} small onClick={() => setEditing((v) => !v)}>
          {editing ? 'Done editing' : 'Edit lines'}
        </Btn>
        <Btn kind="ghost" small disabled={defaultsBusy} onClick={onPersist}>Save as default</Btn>
        {view !== 'source' && (
          <Btn kind={flexOn ? 'gold' : 'ghost'} small onClick={() => setFlexOn((v) => !v)}
            title="Show the Flex sliders in a fixed column beside Line. A fixed line's flex pulls its modelled cost down by up to the season's fall in working turns.">
            {flexOn ? 'Flex on — hide' : 'Enable Flex'}
          </Btn>
        )}
        {data && !data.has_data && <span className="sh-dim" style={{ color: '#b0791f' }}>No P&amp;L data cached for {basis} — refresh it first.</span>}
        <span className="sh-dim" style={{ fontSize: 11 }}>{data?.mix_note}</span>
        <span className="sh-dim" style={{ fontSize: 11 }}>all figures £</span>
      </div>

      {warnings.length > 0 && (
        <div style={{ padding: '0 12px 6px' }}>
          <Callout kind="warn" title="The rolling-stock deductions exceed the P&L hire">
            <ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </Callout>
        </div>
      )}

      {/* TABLE 1 — the source */}
      {view !== 'modelled' && <>
      <div className="sh-section-label" style={{ padding: '4px 12px 0' }}>{view ? '' : '1 · '}Modelling source — actual {basis} P&amp;L</div>
      {renderTable('source')}
      </>}

      {/* TABLE 2 — after the build */}
      {view !== 'source' && <>
      <div className="sh-section-label" style={{ padding: '10px 12px 0' }}>
        {view ? '' : '2 · '}After the build simulation — modelled costs &amp; the differential
      </div>
      <div className="sh-card-note" style={{ padding: '0 12px' }}>
        {hasModel
          ? <>Design activity vs today: turns down <strong>{flexPct}%</strong> — the ceiling for every fixed line's flex slider.</>
          : <>Build the season (Design every month) to see the modelled costs; fixed lines show the basis until then.</>}
        {(activity.proxyRows ?? 0) > 0 && (
          <> <span style={{ color: '#b0791f' }}>{activity.proxyRows === 1
            ? '1 banked month-side predates per-segment mileage — its train-miles use the legacy 24 mi/turn proxy. Rebuild the season to measure it.'
            : `${activity.proxyRows} banked month-sides predate per-segment mileage — their train-miles use the legacy 24 mi/turn proxy. Rebuild the season to measure them.`}</span></>
        )}
      </div>
      {renderTable('modelled')}
      </>}

      <div className="sh-card-foot">
        Variable lines are re-priced at the basis £ per driver-unit × the designed timetable's activity. Fixed lines carry
        through at 100% unless you use their flex slider (Enable Flex, on this tab) — which can never take off more than the {flexPct}% the timetable's
        turns actually fell. Untick a line to drop it from the model entirely. While editing, "+ fixed line" under any
        P&amp;L line adds your own FIXED line beneath it — named, a Starter £ laid Flat (the same twelfth every month) or on
        the line's Cost-curve — and takes it off that line; the grey line beneath shows the deducted lines against the
        original P&amp;L figure, month by month, so you can adjust until they match. Under Hire of Rolling Stock, the named engines
        (from Cost model → Rolling stock in use) and the Fixed Rolling Stock spread are FIXED commitments taken off the P&amp;L
        hire; what remains is Variable after Fixed, still variable by working turns, and an engine's miles above its
        contracted minimum are priced by its contract. These two tables drive the P&amp;L, the roll-up
        and the board paper; the legacy rate-card panels below are kept for reference.
      </div>
    </section>
  )

  // --------------------------------------------------------------------------
  function renderTable(which: 'source' | 'modelled') {
    return (
      <FrozenScroll>
        <table className={`stu-table sh-cost-table${flexOn && which === 'modelled' ? ' sh-cost-table--flex' : ''}`}>
          <thead>
            <tr>
              <th>Line</th>
              {flexOn && which === 'modelled' && <th className="sh-cost-flex">Flex</th>}
              <th className="num sh-cost-season">Season</th>
              {editing && which === 'source' && <th>In</th>}
              <th>Behaviour</th>
              <th title="The per-unit rate — hover a figure for its unit">Rate</th>
              {MONTHS.map((m) => <th key={m} className="num">{m}</th>)}
              {which === 'modelled' && <th className="num">Change</th>}
            </tr>
          </thead>
          <tbody>
            {/* The total leads as well as closes the table (Chris, 2026-10-28:
                "so we don't have to scroll right to the bottom to find it"). */}
            {totalRow(which, true)}
            {groups.map((g) => (
              <Fragment key={g.title}>{groupRows({ title: g.title, rows: g.rows, which })}</Fragment>
            ))}
            {totalRow(which, false)}
          </tbody>
        </table>
      </FrozenScroll>
    )
  }

  function totalRow(which: 'source' | 'modelled', top: boolean) {
    return (
      <tr className={`sh-cost-total${top ? ' sh-cost-total--top' : ''}`}>
        <td><strong>Total (included)</strong></td>
        {flexOn && which === 'modelled' && <td className="sh-cost-flex" />}
        <td className="num sh-mono sh-cost-season"><strong>{gbp0(which === 'source' ? inclTotalBasis : inclTotalModel)}</strong></td>
        {editing && which === 'source' && <td />}
        <td />
        <td />
        {MONTHS.map((_, i) => (
          <td key={i} className="num sh-mono"><strong>{gbp0(which === 'source' ? sim.basisMonthTotals[i] : sim.modelMonthTotals[i])}</strong></td>
        ))}
        {which === 'modelled' && <td className="num sh-mono"><strong>{gbp0(inclTotalModel - inclTotalBasis)}</strong></td>}
      </tr>
    )
  }

  // A plain render helper, CALLED — never mounted as <GroupRows/>. Declared
  // inside this component, a capitalised function is a NEW component type on
  // every render, so React threw away and rebuilt every row on each keystroke:
  // a fixed line's name/Starter box lost focus and the page jumped to the top
  // (Chris, 2026-10-27). No hooks here, so calling it is safe.
  function groupRows({ title, rows, which }: { title: string; rows: LineResult[]; which: 'source' | 'modelled' }) {
    if (rows.length === 0 && which === 'source') return null
    const showFlex = flexOn && which === 'modelled'
    const span = 3 + MONTHS.length + 1 + (editing && which === 'source' ? 1 : 0) + (which === 'modelled' ? 1 : 0) + (showFlex ? 1 : 0)
    return (
      <>
        {/* Title in a normal-width FIRST cell (+ a spanning filler) so it pins
            with the frozen column — a full-width colSpan cell cannot stick. */}
        <tr className="sh-cost-group"><td><strong>{title}</strong></td>{showFlex && <td className="sh-cost-flex" />}<td className="sh-cost-season" /><td colSpan={span - 2 - (showFlex ? 1 : 0)} /></tr>
        {rows.map((r) => {
          const l = r.line
          const months = which === 'source' ? r.basisMonths : r.modelMonths
          const season = which === 'source' ? r.basisTotal : r.modelTotal
          const dim = l.included ? undefined : { opacity: 0.4 }
          const fx = l.customFixed ? fixedById.get(l.id) : undefined
          const editCells = editing && which === 'source'
          // The grey reconciliation line under a P&L line that carries deductions.
          const parentId = l.residualOf ?? (l.rsResidual ? l.id : null)
          const recon = which === 'source' && parentId ? pnlReconciliation(sim, parentId) : null
          const signed = (d: number) => (Math.abs(d) < 0.5 ? '—' : `${d > 0 ? '+' : '−'}${gbp0(Math.abs(d))}`)
          return (
            <Fragment key={l.id}>
            <tr style={dim}>
              <td>
                {fx && editCells ? (
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="text" value={fx.label} placeholder="Name this fixed line" onChange={(e) => patchFixedLine(fx.id, { label: e.target.value })} style={{ width: 150, fontSize: 11 }} />
                    <PillGroup value={fx.mode} onChange={(m) => patchFixedLine(fx.id, { mode: m })}
                      title="Flat: the same twelfth every month of the season. Cost-curve: the Starter laid pro rata to the P&L line's own months."
                      options={[{ value: 'flat' as const, label: 'Flat' }, { value: 'curve' as const, label: 'Cost-curve' }]} />
                    <Btn kind="quiet" small title="Remove this fixed line" onClick={() => removeFixedLine(fx.id)}>×</Btn>
                  </span>
                ) : (
                  <>
                    {l.label}
                    {l.seasonal && <span className="sh-dim" style={{ fontSize: 10 }}> · seasonal</span>}
                    {l.rsMemo && <span className="sh-dim" style={{ fontSize: 10 }}> · memo, not in the totals</span>}
                    {l.memoOf && !l.rsMemo && <span className="sh-dim" style={{ fontSize: 10 }}> · memo, not in the totals</span>}
                    {l.engineContract && <span className="sh-dim" style={{ fontSize: 10 }}> · by contract</span>}
                    {fx && <span className="sh-dim" style={{ fontSize: 10 }}> · fixed, {fx.mode === 'flat' ? 'flat' : 'on the P&L curve'}</span>}
                  </>
                )}
                {editCells && canHost(l) && (
                  <Btn kind="quiet" small title={`Add a custom fixed line under "${l.label}" — named, Flat or Cost-curve, with a Starter £; it comes off this line.`} onClick={() => addFixedLine(l.residualOf ?? l.id)}>+ fixed line</Btn>
                )}
              </td>
              {showFlex && (
                <td className="sh-cost-flex">
                  {l.engineContract || l.engineId || l.id === RS_SPREAD_ID || (isRolling(l) && l.behaviour === 'fixed') ? (
                    <span className="sh-dim" style={{ fontSize: 10 }}
                      title="A rolling-stock contract commitment (Cost model → Rolling stock in use): fixed by its terms, so it does not flex with the timetable. Its miles above the contracted minimum are priced by the contract.">contract</span>
                  ) : l.behaviour === 'fixed' && sim.flexCap > 0.001 ? (
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input type="range" min={0} max={1} step={0.05} value={l.flex ?? 0}
                        onChange={(e) => setFlex(l, parseFloat(e.target.value))} style={{ width: 84 }} />
                      <span className="sh-mono" style={{ fontSize: 10, width: 34, textAlign: 'right' }}>−{Math.round((l.flex ?? 0) * sim.flexCap * 100)}%</span>
                    </span>
                  ) : <span className="sh-dim" style={{ fontSize: 10 }}>{l.behaviour === 'variable' ? 'auto' : '—'}</span>}
                </td>
              )}
              <td className="num sh-mono sh-cost-season"><strong>{gbp0(season)}</strong></td>
              {editCells && (
                <td>
                  {isRolling(l) || fx || l.memoOf
                    ? <span className="sh-dim" style={{ fontSize: 10 }} title={fx ? 'Remove the line with × — it is yours.' : 'Rolling-stock lines are switched on Cost model → Rolling stock in use (Use in simulation) and by the Spread cell.'}>—</span>
                    : <input type="checkbox" checked={l.included} onChange={() => toggleInclude(l)} />}
                </td>
              )}
              <td>
                {editing && !isRolling(l) && !fx && !l.memoOf ? (
                  <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <select value={l.behaviour} onChange={(e) => setBehaviour(l, e.target.value as CostBehaviour)} style={{ fontSize: 11 }}>
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                    </select>
                    {l.behaviour === 'variable' && (
                      <select value={l.driver} onChange={(e) => setDriver(l, e.target.value as CostDriver)} style={{ fontSize: 11 }}>
                        {DRIVERS.map((d) => <option key={d} value={d}>{DRIVER_LABEL[d]}</option>)}
                      </select>
                    )}
                  </span>
                ) : (
                  <span className="sh-dim" style={{ fontSize: 11 }}>
                    {l.engineContract ? 'var · contract' : l.behaviour === 'fixed' ? 'fixed' : `var · ${DRIVER_LABEL[l.driver]}`}
                  </span>
                )}
              </td>
              {/* The one per-unit rate that prices BOTH tables — the planner's
                  double-check (Chris, 2026-09-02): source and modelled must show
                  the identical figure, because the modelled column is just this
                  rate × the designed activity. */}
              <td className="sh-mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {l.id === RS_SPREAD_ID && which === 'source' ? (
                  <label title="Spread: a season £ of hire that is really fixed — contracted minimums not yet named engine by engine. Shared equally over the running months and taken off the P&L hire; type into a month cell to set that month.">
                    <input type="number" min={0} step={500} value={rs.spread ?? 0} aria-label="Spread £ for the season"
                      onChange={(e) => setSpread(Number(e.target.value))} style={{ width: 64 }} />
                  </label>
                ) : fx && which === 'source' ? (
                  <label title="Starter: the season £ of this fixed line, laid across the months Flat or on the P&L line's Cost-curve, and taken off the line above.">
                    <input type="number" min={0} step={500} value={fx.starter} aria-label="Starter £ for the season"
                      onChange={(e) => patchFixedLine(fx.id, { starter: Math.max(0, Number(e.target.value) || 0) })} style={{ width: 64 }} />
                  </label>
                ) : r.rateLabel ? <span title={r.rateLabel}>{rateNum(r.rateLabel)}</span> : <span className="sh-dim">—</span>}
              </td>
              {months.map((v, i) => (
                <td key={i} className="num sh-mono">
                  {l.id === RS_SPREAD_ID && which === 'source' ? (
                    <input type="number" min={0} step={100} value={Math.round(v)}
                      title={rs.spreadMonths?.[i] != null ? 'Typed for this month — clear it to return to the equal share' : 'Equal share of the Spread — type to set this month'}
                      onChange={(e) => setSpreadCell(i, e.target.value === '' ? null : Number(e.target.value))}
                      style={{ width: 60, textAlign: 'right', fontWeight: rs.spreadMonths?.[i] != null ? 700 : 400 }} />
                  ) : (v ? gbp0(v) : '—')}
                </td>
              ))}
              {which === 'modelled' && (
                <td className="num sh-mono" style={{ color: r.delta < -0.5 ? '#2e7d32' : r.delta > 0.5 ? '#b03535' : undefined }}>
                  {Math.abs(r.delta) < 0.5 ? '—' : (r.delta > 0 ? '+' : '') + gbp0(r.delta)}
                </td>
              )}
            </tr>
            {recon && (
              // The fixed lines above, summed, against the accounts figure —
              // + over, − under, — matched (Chris: "so I can adjust the costs
              // until they match").
              <tr className="sh-cost-recon" style={{ opacity: 0.7 }}>
                <td className="sh-dim" style={{ fontSize: 10 }} title="The deducted lines above summed against the original P&L line, month by month: + they exceed it, − they fall short (the shortfall stays on the line above), — they match.">
                  ↳ against the P&amp;L line
                </td>
                <td className="num sh-mono sh-dim sh-cost-season" style={{ fontSize: 10 }}>{signed(recon.total)}</td>
                {editCells && <td />}
                <td className="sh-dim" style={{ fontSize: 10 }}>+ over · − under</td>
                <td />
                {recon.diff.map((d, i) => <td key={i} className="num sh-mono sh-dim" style={{ fontSize: 10 }}>{signed(d)}</td>)}
              </tr>
            )}
            </Fragment>
          )
        })}
      </>
    )
  }
}

/**
 * The cost grid's scroll pane (Chris, 2026-10-28: "another UI nightmare having
 * to continuously scroll down to scroll left or right"). The grid is held to
 * the window's height, with its column headings and the top Total row frozen,
 * and a second horizontal scrollbar sits at its TOP, linked to the grid.
 */
function FrozenScroll({ children }: { children: ReactNode }) {
  const top = useRef<HTMLDivElement>(null)
  const body = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const syncing = useRef(false)
  useEffect(() => {
    const el = body.current
    if (!el) return
    // + the grid's vertical scrollbar: the top bar is that much wider than the
    // grid's visible area, so without it the top bar stopped short of the
    // last column (Chris, 2026-10-28: "scroll bar is cutting off").
    const measure = () => setWidth(el.scrollWidth + Math.max(0, el.offsetWidth - el.clientWidth))
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    if (el.firstElementChild) ro.observe(el.firstElementChild)
    return () => ro.disconnect()
  }, [])
  const follow = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to) return
    if (syncing.current) { syncing.current = false; return }
    syncing.current = true
    to.scrollLeft = from.scrollLeft
  }
  return (
    <div className="sh-fscroll">
      <div className="sh-fscroll-top" ref={top} onScroll={() => follow(top.current, body.current)} aria-hidden="true">
        <div style={{ width, height: 1 }} />
      </div>
      <div className="sh-fscroll-body" ref={body} onScroll={() => follow(body.current, top.current)}>
        {children}
      </div>
    </div>
  )
}
